/**
 * Last-Minute Insertion Module - Phase 4
 * Last-Minute Insertion Implementation
 *
 * Combines all pieces into three-tier insertion strategy:
 * - Tier 1: Zero-disruption (try with all existing locked)
 * - Tier 2: Local repair (allow limited moves with cascade)
 * - Tier 3: Failed (report impossibility with analysis)
 */

import { ParkingRequest, Spot, Placement, LogEntry, Zone } from "./types";
import { parkingAlgorithm, getDecisionLog } from "./parkingAlgorithm";
import { findRepairNeighborhood } from "./neighborhoodSelection";
import { tryCascadeRepair, CascadeMove } from "./cascadeRepair";

/**
 * Configuration options for last-minute insertion
 */
export interface LastMinuteInsertionOptions {
  /** Maximum number of aircraft moves allowed (0-5, default: 2) */
  maxDisruption: number;

  /** Weight for stickiness penalty (default: 10.0) */
  stickinessWeight?: number;

  /** Allow trapping other aircraft (default: false for last-minute) */
  allowTrapping?: boolean;

  /** Weight for egress penalty (default: 5.0) */
  egressWeight?: number;

  /** Buffer time in minutes (default: 15) */
  bufferMinutes?: number;

  /** Enable local repair tier (default: true) */
  tryLocalRepair?: boolean;

  /** Planning horizon in hours (default: 24) */
  horizonHours?: number;
}

/**
 * Result of a last-minute insertion attempt
 */
export interface InsertionResult {
  /** Whether insertion succeeded */
  success: boolean;

  /** Which tier succeeded/failed */
  tier: "zero-disruption" | "local-repair" | "failed";

  /** Final placement (if successful) */
  placement?: Placement;

  /** IDs of aircraft that were moved */
  disruptedAircraft: string[];

  /** Total number of moves performed */
  totalMoves: number;

  /** Decision log entries */
  decisionLog: LogEntry[];

  /** Analysis of attempt */
  analysis: {
    /** Did we try zero-disruption? */
    attemptedZeroDisruption: boolean;

    /** Did we try local repair? */
    attemptedLocalRepair: boolean;

    /** Why did it fail? (if failed) */
    failureReason?: string;
  };
}

/**
 * Attempt to insert a new aircraft into existing schedule
 *
 * Three-tier strategy:
 * 1. Zero-disruption: Try with all existing aircraft locked
 * 2. Local repair: Allow limited moves using cascade repair
 * 3. Failed: Report why it couldn't fit
 *
 * @param newTrip New aircraft to insert
 * @param existingPlacements Current placement of existing aircraft
 * @param spots Available spots on ramp (bitmaps must already reflect existing placements)
 * @param allRequests Existing aircraft only (NOT including newTrip)
 * @param options Configuration options
 * @returns InsertionResult with success status and details
 */
export function attemptLastMinuteInsertion(
  newTrip: ParkingRequest,
  existingPlacements: Placement,
  spots: Spot[],
  allRequests: ParkingRequest[],
  options: LastMinuteInsertionOptions
): InsertionResult {
  const {
    maxDisruption,
    stickinessWeight = 10.0,
    allowTrapping = false,
    egressWeight = 5.0,
    bufferMinutes = 15,
    tryLocalRepair = true,
    horizonHours = 24,
  } = options;

  // Validate maxDisruption range (0-5 per design spec)
  if (maxDisruption < 0 || maxDisruption > 5) {
    throw new Error(
      `maxDisruption must be between 0-5, got ${maxDisruption}`
    );
  }

  // Clone spots for Tier 1 to prevent bitmap corruption
  // parkingAlgorithm mutates occupancy bitmaps, so we need clean state for Tier 2
  const spotsCopyTier1 = spots.map((s) => ({
    ...s,
    occupancy: new BigUint64Array(s.occupancy),
  }));

  // TIER 1: Zero-disruption attempt
  const zeroResult = tryZeroDisruptionPlacement(
    newTrip,
    existingPlacements,
    spotsCopyTier1, // Use copy to prevent corruption
    { bufferMinutes, egressWeight, allowTrapping, horizonHours }
  );

  if (zeroResult.success) {
    return {
      success: true,
      tier: "zero-disruption",
      placement: zeroResult.placement,
      disruptedAircraft: [],
      totalMoves: 0,
      decisionLog: zeroResult.decisionLog,
      analysis: {
        attemptedZeroDisruption: true,
        attemptedLocalRepair: false,
      },
    };
  }

  // TIER 2: Local repair (if enabled and budget allows)
  if (tryLocalRepair && maxDisruption > 0) {
    const repairResult = tryLocalRepairInsertion(
      newTrip,
      existingPlacements,
      spots,
      allRequests,
      {
        maxDisruption,
        stickinessWeight,
        egressWeight,
        bufferMinutes,
        allowTrapping,
      }
    );

    if (repairResult.success && repairResult.totalMoves <= maxDisruption) {
      // Combine zero-disruption log (NEW's attempt) with repair log (moves)
      const combinedLog = [...zeroResult.decisionLog, ...repairResult.decisionLog];

      return {
        success: true,
        tier: "local-repair",
        placement: repairResult.placement,
        disruptedAircraft: repairResult.disruptedAircraft,
        totalMoves: repairResult.totalMoves,
        decisionLog: combinedLog,
        analysis: {
          attemptedZeroDisruption: true,
          attemptedLocalRepair: true,
        },
      };
    }
  }

  // TIER 3: Failed
  const failureReason = generateFailureReason(
    newTrip,
    spots,
    allRequests,
    maxDisruption,
    zeroResult.decisionLog
  );

  return {
    success: false,
    tier: "failed",
    disruptedAircraft: [],
    totalMoves: 0,
    decisionLog: zeroResult.decisionLog,
    analysis: {
      attemptedZeroDisruption: true,
      attemptedLocalRepair: tryLocalRepair && maxDisruption > 0,
      failureReason,
    },
  };
}

/**
 * Tier 1: Try zero-disruption placement
 * Tries to place new trip WITHOUT moving any existing aircraft
 * Assumes spots' bitmaps already reflect existing placements
 */
function tryZeroDisruptionPlacement(
  newTrip: ParkingRequest,
  existingPlacements: Placement,
  spots: Spot[],
  options: {
    bufferMinutes: number;
    egressWeight: number;
    allowTrapping: boolean;
    horizonHours: number;
  }
): {
  success: boolean;
  placement?: Placement;
  decisionLog: LogEntry[];
} {
  // Run algorithm with ONLY the new trip
  // Existing aircraft's spots are already marked occupied in bitmaps
  const zones: Zone[] = [];
  const placement = parkingAlgorithm(
    [newTrip], // ONLY new trip
    spots,
    options.bufferMinutes,
    0, // plannedMoveCap
    options.allowTrapping,
    options.egressWeight,
    1.0, // wasteWeight - standard waste penalty
    0.1, // structuralWeight - tiebreaker for spot complexity
    "arrival",
    options.horizonHours,
    false, // useMaskOptimization
    false, // validateMasks
    1.0, // zonePriorityWeight - standard zone preference
    zones,
    2.0, // min_dwell_weight - standard min dwell enforcement
    0.0 // No stickiness for new trip (no previous placement)
  );

  const log = getDecisionLog();

  // Check if new trip was placed
  const newTripPlaced = placement[newTrip.id] !== undefined;

  // Merge with existing placements if successful
  const finalPlacement = newTripPlaced
    ? { ...existingPlacements, ...placement }
    : undefined;

  return {
    success: newTripPlaced,
    placement: finalPlacement,
    decisionLog: log,
  };
}

/**
 * Tier 2: Try local repair with cascades
 * Uses neighborhood selection and cascade repair
 */
function tryLocalRepairInsertion(
  newTrip: ParkingRequest,
  existingPlacements: Placement,
  spots: Spot[],
  allRequests: ParkingRequest[],
  options: {
    maxDisruption: number;
    stickinessWeight: number;
    egressWeight: number;
    bufferMinutes: number;
    allowTrapping: boolean;
  }
): {
  success: boolean;
  placement?: Placement;
  disruptedAircraft: string[];
  totalMoves: number;
  decisionLog: LogEntry[];
} {
  // Find candidate aircraft to move
  const candidates = findRepairNeighborhood(
    newTrip,
    existingPlacements,
    spots,
    allRequests
  );

  if (candidates.length === 0) {
    // No candidates (all aircraft in incompatible spots)
    return {
      success: false,
      disruptedAircraft: [],
      totalMoves: 0,
      decisionLog: [],
    };
  }

  // Try cascade repair with each candidate (sorted by moveability)
  // No need to clone - tryCascadeRepair handles rollback with TransactionLog
  for (const candidate of candidates) {
    // Create working copies of placement and spots for this attempt
    // Deep clone placement (simple object structure)
    const placementCopy: Placement = {};
    for (const [id, place] of Object.entries(existingPlacements)) {
      if ("spotId" in place) {
        placementCopy[id] = { spotId: place.spotId, orientationId: place.orientationId };
      } else {
        placementCopy[id] = {
          segments: place.segments.map(seg => ({ ...seg })),
          moveCount: place.moveCount,
        };
      }
    }

    // Clone spots (including shape reference - shallow copy is fine)
    const spotsCopy = spots.map((s) => ({
      ...s,
      occupancy: new BigUint64Array(s.occupancy),
      // shape is read-only, shallow copy is fine
    }));

    const result = tryCascadeRepair(
      candidate,
      newTrip,
      placementCopy,
      spotsCopy,
      allRequests,
      { maxRepairMoves: options.maxDisruption, maxDepth: 3 },
      [],
      0
    );

    if (result.success && result.moves.length <= options.maxDisruption) {
      // Success! Build decision log entries for moves
      const moveLog: LogEntry[] = result.moves.map((move) => ({
        aircraftId: move.aircraft.id,
        spotId: move.toSpot,
        type: "moved" as const,
        fromSpot: move.fromSpot,
        toSpot: move.toSpot,
        moveReason: "Last-minute insertion cascade",
      }));

      return {
        success: true,
        placement: placementCopy,
        disruptedAircraft: result.moves.map((m) => m.aircraft.id),
        totalMoves: result.moves.length,
        decisionLog: moveLog,
      };
    }
    // tryCascadeRepair rolled back on failure, spotsCopy discarded
    // Continue to next candidate
  }

  // All candidates failed
  return {
    success: false,
    disruptedAircraft: [],
    totalMoves: 0,
    decisionLog: [],
  };
}

/**
 * Generate helpful failure reason
 */
function generateFailureReason(
  newTrip: ParkingRequest,
  spots: Spot[],
  allRequests: ParkingRequest[],
  maxDisruption: number,
  decisionLog: LogEntry[]
): string {
  // Find compatible spots
  const compatibleSpots = spots.filter((s) =>
    s.allowedSizes.includes(newTrip.sizeCategory)
  );

  if (compatibleSpots.length === 0) {
    return `No spots on ramp can accommodate ${newTrip.sizeCategory} aircraft`;
  }

  // Check if it's a budget issue
  if (maxDisruption === 0) {
    return `Cannot fit without disruption. All ${compatibleSpots.length} compatible spots are occupied. Increase maxDisruption to allow moves.`;
  }

  // General failure
  return `Cannot fit within ${maxDisruption} move budget. Try increasing maxDisruption or adjusting arrival/departure times.`;
}
